import { usePreloadedQuery, type PreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { parseRoute } from "src/wac-routes";

import { agileArticleOneQuery$data } from "@/__generated_contentful__/agileArticleOneQuery.graphql";
import { agileArticleOneQuery } from "@/__generated_contentful__/agileArticleOneQuery.graphql";
import { agileArticleTwoQuery$data } from "@/__generated_contentful__/agileArticleTwoQuery.graphql";
import { WAC_TO_CPAPI_LOCALE_MAP } from "@/utils/content-queries/constants";

import { richTextToHTML } from "../../clients/uniform-client/enhancers/utils";
import {
    AgileArticle,
    ContentComponent,
    CallToAction,
    TextComponent,
    AssetComponent,
    ProTipComponent,
    TwitterComponent,
    CallOutComponent,
    QuestionComponent,
    EmbedComponent,
    NextBestAction
} from "../../templates/agile-hub/types";

import type { OperationType } from "relay-runtime";

type Merge<T, U> = T & U;
type Combined = Merge<
    NonNullable<
        NonNullable<
            agileArticleOneQuery$data["articleDetailsCollection"]
        >["items"]
    >[number],
    NonNullable<
        NonNullable<
            agileArticleTwoQuery$data["articleDetailsCollection"]
        >["items"]
    >[number]
>;
export type AgileArticleQueryData = {
    readonly articleDetailsCollection: {
        items: ReadonlyArray<Combined>;
    };
};

export const AgileArticleOneQuery = graphql`
    query agileArticleOneQuery(
        $limit: Int = 1
        $urlSlug: String!
        $locale: String!
    ) {
        articleDetailsCollection(
            limit: $limit
            where: { articleRef: { urlSlug: $urlSlug } }
            locale: $locale
        ) {
            items {
                subtitle: articleSubtitle
                articleSummary {
                    json
                }
                contentHubSubscribeCollection(limit: 3) {
                    items {
                        subscribeComponentName: subscribeComponentNameExternal
                        valueProposition
                        ctaButtonText
                        successMessage
                    }
                }
                articleRef {
                    articleName: articleNameExternal
                    title: articleTitle
                    description
                    urlSlug
                    articleBanner {
                        description
                        name: title
                        url
                        width
                        height
                        size
                        fileName
                        contentType
                    }
                }
                author {
                    name: authorNameExternal
                    jobTitle
                    shortBiography
                    authorPicture {
                        description
                        name: title
                        url
                        width
                        height
                        size
                        fileName
                        contentType
                    }
                }
                relatedHubCollection(limit: 1) {
                    items {
                        title: contentHubTitleExternal
                        shortDescriptionOneLiner
                        description {
                            json
                        }
                    }
                }
                articleIntroductionCollection(limit: 1) {
                    items {
                        articleIntroductionName: articleIntroductionTitleExternal
                        articleIntroductionDetails {
                            json
                        }
                        embedLink
                        articleIntroductionAsset {
                            description
                            name: title
                            url
                            width
                            height
                            size
                            fileName
                            contentType
                        }
                        componentizedIntroductionCollection(limit: 10) {
                            items {
                                ... on AssetComponent {
                                    __typename
                                    caption: assetCaption
                                    assetRelatedText {
                                        json
                                    }
                                    asset {
                                        description
                                        name: title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                }
                                ... on ProTipComponent {
                                    __typename
                                    name: proTipNameExternal
                                    proTipText
                                }
                                ... on TextComponent {
                                    __typename
                                    name: textComponentNameExternal
                                    text {
                                        json
                                    }
                                }
                                ... on CallToAction {
                                    __typename
                                    title: ctaTitleExternal
                                    valueProposition
                                    ctaText
                                    url: ctaUrl
                                    productCollection(limit: 1) {
                                        items {
                                            productNameReference {
                                                productName
                                            }
                                            productLogo: icon {
                                                description
                                                name: title
                                                url
                                                width
                                                height
                                                size
                                                fileName
                                                contentType
                                            }
                                        }
                                    }
                                    dataBlueprintModule
                                    asset: ctaAsset {
                                        description
                                        name: title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                }
                                ... on QuestionComponent {
                                    __typename
                                    questionTitle: questionTitleExternal
                                    answer {
                                        json
                                    }
                                    answerAsset {
                                        description
                                        name: title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                }
                                ... on TwitterComponent {
                                    __typename
                                    twitterComponentName: twitterComponentExternalName
                                    tweetText
                                    twitterUrl
                                }
                                ... on CallOutComponent {
                                    __typename
                                    title: callOutComponentTitleExternal
                                    callOutText {
                                        json
                                    }
                                    icon: callOutComponentIcon {
                                        description
                                        name: title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                }
                            }
                        }
                    }
                }
                productDiscussedCtaCollection(limit: 1) {
                    items {
                        title: ctaTitleExternal
                        valueProposition
                        text: ctaText
                        url: ctaUrl
                        asset: ctaAsset {
                            description
                            name: title
                            url
                            width
                            height
                            size
                            fileName
                            contentType
                        }
                        productCollection(limit: 1) {
                            items {
                                productNameReference {
                                    productName
                                }
                                productLogo: icon {
                                    description
                                    name: title
                                    url
                                    width
                                    height
                                    size
                                    fileName
                                    contentType
                                }
                            }
                        }
                        dataBlueprintModule
                    }
                }
            }
        }
    }
`;

export const AgileArticleTwoQuery = graphql`
    query agileArticleTwoQuery(
        $limit: Int = 1
        $urlSlug: String!
        $locale: String!
    ) {
        articleDetailsCollection(
            limit: $limit
            where: { articleRef: { urlSlug: $urlSlug } }
            locale: $locale
        ) {
            items {
                bodyTextContainerCollection(limit: 15) {
                    items {
                        name: supportingConceptNameExternal
                        contentComponentsCollection(limit: 20) {
                            items {
                                ... on EmbeddedVideoAsset {
                                    __typename
                                    embeddedVideoAssetName: embeddedVideoAssetNameExternal
                                    embedded
                                    embedAssetOverlay {
                                        description
                                        name: title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                }
                                ... on TextComponent {
                                    __typename
                                    name: textComponentNameExternal
                                    text {
                                        json
                                    }
                                }
                                ... on AssetComponent {
                                    __typename
                                    asset {
                                        description
                                        name: title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                    caption: assetCaption
                                    assetRelatedText {
                                        json
                                    }
                                }
                                ... on ProTipComponent {
                                    __typename
                                    name: proTipNameExternal
                                    proTipText
                                }
                                ... on TwitterComponent {
                                    __typename
                                    twitterComponentName: twitterComponentExternalName
                                    tweetText
                                    twitterUrl
                                }
                                ... on CallToAction {
                                    __typename
                                    title: ctaTitleExternal
                                    ctaText
                                    url: ctaUrl
                                    valueProposition
                                    dataBlueprintModule
                                    asset: ctaAsset {
                                        description
                                        name: title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                    productCollection(limit: 1) {
                                        items {
                                            productNameReference {
                                                productName
                                            }
                                            productLogo: icon {
                                                description
                                                name: title
                                                url
                                                width
                                                height
                                                size
                                                fileName
                                                contentType
                                            }
                                        }
                                    }
                                }
                                ... on CallOutComponent {
                                    __typename
                                    title: callOutComponentTitleExternal
                                    callOutText {
                                        json
                                    }
                                    asset {
                                        description
                                        name: title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                    icon: callOutComponentIcon {
                                        description
                                        name: title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                }
                                ... on QuestionComponent {
                                    __typename
                                    questionTitle: questionTitleExternal
                                    answer {
                                        json
                                    }
                                    answerAsset {
                                        description
                                        name: title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                }
                            }
                        }
                    }
                }
                nextBestActionCollection(limit: 2) {
                    items {
                        ... on MicrositeArticle {
                            __typename
                            articleName: articleNameExternal
                            title: articleTitle
                            description
                            articleBanner {
                                description
                                name: title
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                            urlSlug
                        }
                        ... on Tutorial {
                            __typename
                            name: tutorialNameExternal
                            title: tutorialTitle
                            description: tutorialDescription
                            tutorialBanner {
                                description
                                name: title
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                            urlSlug
                        }
                    }
                }
                upNext {
                    ... on MicrositeTopicOverview {
                        __typename
                        topicOverviewId: _id
                        name: topicNameExternal
                        subtitle: topicSubtitle
                        description
                        bannerImage {
                            description
                            name: title
                            url
                            width
                            height
                            size
                            fileName
                            contentType
                        }
                        urlSlug
                        title: topicTitle
                    }
                    ... on MicrositeArticle {
                        __typename
                        articleName: articleNameExternal
                        articleTitle
                        description
                        articleBanner {
                            description
                            name: title
                            url
                            width
                            height
                            size
                            fileName
                            contentType
                        }
                        urlSlug
                    }
                    ... on Tutorial {
                        __typename
                        tutorialName: tutorialNameExternal
                        tutorialTitle
                        description: tutorialDescription
                        tutorialBanner {
                            description
                            name: title
                            url
                            width
                            height
                            size
                            fileName
                            contentType
                        }
                        urlSlug
                    }
                }
            }
        }
    }
`;

export const getAgileArticleQueryVars = (location: Location) => {
    const { locale, path } = parseRoute(location);

    return {
        limit: 1,
        urlSlug: path ? `/${path}` : "",
        locale: WAC_TO_CPAPI_LOCALE_MAP[locale] || "en-US"
    };
};

/**
 * Transforms Agile Article Response from Contentful to
 * the shape that matches deprecated CPAPI response
 */

export const shapeAgileArticleContentfulResponse = (
    data: AgileArticleQueryData
): AgileArticle => {
    return (
        // eslint-disable-next-line complexity
        data.articleDetailsCollection?.items.map((articleDetails) => ({
            subtitle: articleDetails?.subtitle || null,
            articleSummary: articleDetails?.articleSummary?.json
                ? richTextToHTML(articleDetails?.articleSummary?.json)
                : null,
            articleRef: articleDetails?.articleRef,
            author: {
                authorPicture: {
                    contentType:
                        articleDetails?.author?.authorPicture?.contentType ||
                        undefined,
                    description:
                        articleDetails?.author?.authorPicture?.description ||
                        undefined,
                    fileName: articleDetails?.author?.authorPicture?.fileName,
                    height:
                        articleDetails?.author?.authorPicture?.height ||
                        undefined,
                    name: articleDetails?.author?.authorPicture?.name || "",
                    size: articleDetails?.author?.authorPicture?.size || 0,
                    url: articleDetails?.author?.authorPicture?.url || "",
                    width:
                        articleDetails?.author?.authorPicture?.width ||
                        undefined
                },
                jobTitle: articleDetails?.author?.jobTitle,
                name: articleDetails?.author?.name || "",
                shortBiography: articleDetails?.author?.shortBiography
            },
            articleIntroduction: (() => {
                const mapped =
                    articleDetails?.articleIntroductionCollection?.items
                        .map(
                            // eslint-disable-next-line complexity
                            (aIntro) => ({
                                articleIntroductionName:
                                    aIntro?.articleIntroductionName || null,
                                articleIntroductionDetails: aIntro
                                    ?.articleIntroductionDetails?.json
                                    ? richTextToHTML(
                                          aIntro?.articleIntroductionDetails
                                              ?.json
                                      )
                                    : null,
                                embedLink: aIntro?.embedLink || null,
                                articleIntroductionAsset: aIntro
                                    ?.articleIntroductionAsset?.url
                                    ? {
                                          description:
                                              aIntro?.articleIntroductionAsset
                                                  ?.description || undefined,
                                          name:
                                              aIntro?.articleIntroductionAsset
                                                  ?.name || "",
                                          url:
                                              aIntro?.articleIntroductionAsset
                                                  ?.url || "",
                                          width:
                                              aIntro?.articleIntroductionAsset
                                                  ?.width || undefined,
                                          height:
                                              aIntro?.articleIntroductionAsset
                                                  ?.height || undefined,
                                          size:
                                              aIntro?.articleIntroductionAsset
                                                  ?.size || 0,
                                          fileName:
                                              aIntro?.articleIntroductionAsset
                                                  ?.fileName || null,
                                          contentType:
                                              aIntro?.articleIntroductionAsset
                                                  ?.contentType || undefined
                                      }
                                    : null,
                                componentizedIntroduction: (() => {
                                    const mappedComponentizedIntro =
                                        aIntro?.componentizedIntroductionCollection?.items
                                            .map(
                                                // eslint-disable-next-line complexity
                                                (
                                                    aIntroComponent
                                                ): ContentComponent => {
                                                    switch (
                                                        aIntroComponent?.__typename
                                                    ) {
                                                        case "CallToAction":
                                                            return {
                                                                __typename:
                                                                    aIntroComponent?.__typename,
                                                                title:
                                                                    aIntroComponent.title ||
                                                                    null,
                                                                valueProposition:
                                                                    aIntroComponent.valueProposition ||
                                                                    null,
                                                                text:
                                                                    aIntroComponent.ctaText ||
                                                                    null,
                                                                url:
                                                                    aIntroComponent.url ||
                                                                    null,
                                                                asset: aIntroComponent.asset
                                                                    ? {
                                                                          description:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .description ||
                                                                              undefined,
                                                                          name:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .name ||
                                                                              "",
                                                                          url:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .url ||
                                                                              "",
                                                                          width:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .width ||
                                                                              undefined,
                                                                          height:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .height ||
                                                                              undefined,
                                                                          size:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .size ||
                                                                              0,
                                                                          fileName:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .fileName ||
                                                                              null,
                                                                          contentType:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .contentType ||
                                                                              undefined
                                                                      }
                                                                    : null,
                                                                productName:
                                                                    aIntroComponent
                                                                        .productCollection
                                                                        ?.items[0]
                                                                        ?.productNameReference
                                                                        ?.productName ||
                                                                    null,
                                                                productLogo:
                                                                    aIntroComponent
                                                                        .productCollection
                                                                        ?.items[0]
                                                                        ?.productLogo
                                                                        ? {
                                                                              description:
                                                                                  aIntroComponent
                                                                                      .productCollection
                                                                                      .items[0]
                                                                                      .productLogo
                                                                                      .description ||
                                                                                  undefined,
                                                                              name:
                                                                                  aIntroComponent
                                                                                      .productCollection
                                                                                      .items[0]
                                                                                      .productLogo
                                                                                      .name ||
                                                                                  "",
                                                                              url:
                                                                                  aIntroComponent
                                                                                      .productCollection
                                                                                      .items[0]
                                                                                      .productLogo
                                                                                      .url ||
                                                                                  "",
                                                                              width:
                                                                                  aIntroComponent
                                                                                      .productCollection
                                                                                      .items[0]
                                                                                      .productLogo
                                                                                      .width ||
                                                                                  undefined,
                                                                              height:
                                                                                  aIntroComponent
                                                                                      .productCollection
                                                                                      .items[0]
                                                                                      .productLogo
                                                                                      .height ||
                                                                                  undefined,
                                                                              size:
                                                                                  aIntroComponent
                                                                                      .productCollection
                                                                                      .items[0]
                                                                                      .productLogo
                                                                                      .size ||
                                                                                  0,
                                                                              fileName:
                                                                                  aIntroComponent
                                                                                      .productCollection
                                                                                      .items[0]
                                                                                      .productLogo
                                                                                      .fileName ||
                                                                                  null,
                                                                              contentType:
                                                                                  aIntroComponent
                                                                                      .productCollection
                                                                                      .items[0]
                                                                                      .productLogo
                                                                                      .contentType ||
                                                                                  undefined
                                                                          }
                                                                        : null,
                                                                dataBlueprintModule:
                                                                    aIntroComponent.dataBlueprintModule ||
                                                                    null
                                                            } as CallToAction;
                                                        case "TextComponent":
                                                            return {
                                                                __typename:
                                                                    aIntroComponent?.__typename,
                                                                name:
                                                                    aIntroComponent.name ||
                                                                    null,
                                                                text: aIntroComponent
                                                                    .text?.json
                                                                    ? richTextToHTML(
                                                                          aIntroComponent
                                                                              .text
                                                                              .json
                                                                      )
                                                                    : null
                                                            } as TextComponent;
                                                        case "AssetComponent":
                                                            return {
                                                                __typename:
                                                                    aIntroComponent?.__typename,
                                                                asset: aIntroComponent.asset
                                                                    ? {
                                                                          description:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .description ||
                                                                              undefined,
                                                                          name:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .name ||
                                                                              "",
                                                                          url:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .url ||
                                                                              "",
                                                                          width:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .width ||
                                                                              undefined,
                                                                          height:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .height ||
                                                                              undefined,
                                                                          size:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .size ||
                                                                              0,
                                                                          fileName:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .fileName ||
                                                                              null,
                                                                          contentType:
                                                                              aIntroComponent
                                                                                  .asset
                                                                                  .contentType ||
                                                                              undefined
                                                                      }
                                                                    : null,
                                                                caption:
                                                                    aIntroComponent.caption ||
                                                                    null,
                                                                assetRelatedText:
                                                                    aIntroComponent
                                                                        .assetRelatedText
                                                                        ?.json
                                                                        ? richTextToHTML(
                                                                              aIntroComponent
                                                                                  .assetRelatedText
                                                                                  .json
                                                                          )
                                                                        : null
                                                            } as AssetComponent;
                                                        case "ProTipComponent":
                                                            return {
                                                                __typename:
                                                                    aIntroComponent?.__typename,
                                                                name:
                                                                    aIntroComponent.name ||
                                                                    null,
                                                                proTipText:
                                                                    aIntroComponent.proTipText ||
                                                                    null
                                                            } as ProTipComponent;
                                                        case "TwitterComponent":
                                                            return {
                                                                __typename:
                                                                    aIntroComponent?.__typename,
                                                                twitterComponentName:
                                                                    aIntroComponent.twitterComponentName ||
                                                                    null,
                                                                tweetText:
                                                                    aIntroComponent.tweetText ||
                                                                    null,
                                                                twitterUrl:
                                                                    aIntroComponent.twitterUrl ||
                                                                    null
                                                            } as TwitterComponent;
                                                        case "CallOutComponent":
                                                            return {
                                                                __typename:
                                                                    aIntroComponent?.__typename,
                                                                title:
                                                                    aIntroComponent.title ||
                                                                    null,
                                                                callOutText:
                                                                    aIntroComponent
                                                                        .callOutText
                                                                        ?.json
                                                                        ? richTextToHTML(
                                                                              aIntroComponent
                                                                                  .callOutText
                                                                                  .json
                                                                          )
                                                                        : null,
                                                                icon:
                                                                    aIntroComponent.icon ||
                                                                    null
                                                            } as CallOutComponent;
                                                        case "QuestionComponent":
                                                            return {
                                                                __typename:
                                                                    aIntroComponent?.__typename,
                                                                questionTitle:
                                                                    aIntroComponent.questionTitle ||
                                                                    null,
                                                                answer: aIntroComponent
                                                                    .answer
                                                                    ?.json
                                                                    ? richTextToHTML(
                                                                          aIntroComponent
                                                                              .answer
                                                                              .json
                                                                      )
                                                                    : null,
                                                                answerAsset:
                                                                    aIntroComponent.answerAsset ||
                                                                    null
                                                            } as QuestionComponent;
                                                        default:
                                                            return {
                                                                __typename:
                                                                    aIntroComponent?.__typename,
                                                                title: null,
                                                                valueProposition:
                                                                    null,
                                                                text: null,
                                                                url: null,
                                                                asset: null,
                                                                productName:
                                                                    null,
                                                                productLogo:
                                                                    null,
                                                                dataBlueprintModule:
                                                                    null
                                                            } as CallToAction;
                                                    }
                                                }
                                            )
                                            ?.filter(Boolean) || null;

                                    return mappedComponentizedIntro?.length
                                        ? mappedComponentizedIntro
                                        : null;
                                })()
                            })
                        )
                        ?.filter(Boolean) || null;

                return mapped?.length ? mapped : null;
            })(),
            bodyTextContainer:
                articleDetails?.bodyTextContainerCollection?.items?.map(
                    (container) => {
                        if (!container) {
                            return {
                                name: "",
                                contentComponents: []
                            };
                        }
                        return {
                            name: container.name || "",
                            contentComponents:
                                container.contentComponentsCollection?.items?.map(
                                    // eslint-disable-next-line complexity
                                    (component) => {
                                        switch (component?.__typename) {
                                            case "TextComponent":
                                                return {
                                                    __typename:
                                                        component.__typename,
                                                    name: component.name || "",
                                                    text: component.text?.json
                                                        ? richTextToHTML(
                                                              component.text
                                                                  ?.json
                                                          )
                                                        : null
                                                } as TextComponent;
                                            case "AssetComponent":
                                                return {
                                                    __typename:
                                                        component.__typename,
                                                    asset: component.asset,
                                                    caption:
                                                        component.caption ||
                                                        null,
                                                    assetRelatedText: component
                                                        .assetRelatedText?.json
                                                        ? richTextToHTML(
                                                              component
                                                                  .assetRelatedText
                                                                  ?.json
                                                          )
                                                        : null
                                                } as AssetComponent;
                                            case "ProTipComponent":
                                                return {
                                                    __typename:
                                                        component.__typename,
                                                    name: component.name || "",
                                                    proTipText:
                                                        component.proTipText ||
                                                        ""
                                                } as ProTipComponent;
                                            case "TwitterComponent":
                                                return {
                                                    __typename:
                                                        component.__typename,
                                                    twitterComponentName:
                                                        component.twitterComponentName ||
                                                        "",
                                                    tweetText:
                                                        component.tweetText ||
                                                        "",
                                                    twitterUrl:
                                                        component.twitterUrl ||
                                                        ""
                                                } as TwitterComponent;
                                            case "CallOutComponent":
                                                return {
                                                    __typename:
                                                        "CallOutComponent",
                                                    title:
                                                        component.title || "",
                                                    callOutText: component
                                                        .callOutText?.json
                                                        ? richTextToHTML(
                                                              component
                                                                  .callOutText
                                                                  ?.json
                                                          )
                                                        : null,
                                                    asset: component.asset
                                                        ? {
                                                              description:
                                                                  component
                                                                      .asset
                                                                      .description ||
                                                                  undefined,
                                                              name:
                                                                  component
                                                                      .asset
                                                                      .name ||
                                                                  "",
                                                              url:
                                                                  component
                                                                      .asset
                                                                      .url ||
                                                                  "",
                                                              width:
                                                                  component
                                                                      .asset
                                                                      .width ||
                                                                  undefined,
                                                              height:
                                                                  component
                                                                      .asset
                                                                      .height ||
                                                                  undefined,
                                                              size:
                                                                  component
                                                                      .asset
                                                                      .size ||
                                                                  0,
                                                              fileName:
                                                                  component
                                                                      .asset
                                                                      .fileName ||
                                                                  null,
                                                              contentType:
                                                                  component
                                                                      .asset
                                                                      .contentType ||
                                                                  undefined
                                                          }
                                                        : null,
                                                    icon: component.icon || null
                                                } as CallOutComponent;
                                            case "CallToAction":
                                                return {
                                                    __typename:
                                                        component?.__typename,
                                                    title:
                                                        component.title || null,
                                                    valueProposition:
                                                        component.valueProposition ||
                                                        null,
                                                    text:
                                                        component.ctaText ||
                                                        null,
                                                    url: component.url || null,
                                                    asset: component.asset
                                                        ? {
                                                              description:
                                                                  component
                                                                      .asset
                                                                      .description ||
                                                                  undefined,
                                                              name:
                                                                  component
                                                                      .asset
                                                                      .name ||
                                                                  "",
                                                              url:
                                                                  component
                                                                      .asset
                                                                      .url ||
                                                                  "",
                                                              width:
                                                                  component
                                                                      .asset
                                                                      .width ||
                                                                  undefined,
                                                              height:
                                                                  component
                                                                      .asset
                                                                      .height ||
                                                                  undefined,
                                                              size:
                                                                  component
                                                                      .asset
                                                                      .size ||
                                                                  0,
                                                              fileName:
                                                                  component
                                                                      .asset
                                                                      .fileName ||
                                                                  null,
                                                              contentType:
                                                                  component
                                                                      .asset
                                                                      .contentType ||
                                                                  undefined
                                                          }
                                                        : null,
                                                    productName:
                                                        component
                                                            .productCollection
                                                            ?.items[0]
                                                            ?.productNameReference
                                                            ?.productName ||
                                                        null,
                                                    productLogo: component
                                                        .productCollection
                                                        ?.items[0]?.productLogo
                                                        ? {
                                                              description:
                                                                  component
                                                                      .productCollection
                                                                      .items[0]
                                                                      .productLogo
                                                                      .description ||
                                                                  undefined,
                                                              name:
                                                                  component
                                                                      .productCollection
                                                                      .items[0]
                                                                      .productLogo
                                                                      .name ||
                                                                  "",
                                                              url:
                                                                  component
                                                                      .productCollection
                                                                      .items[0]
                                                                      .productLogo
                                                                      .url ||
                                                                  "",
                                                              width:
                                                                  component
                                                                      .productCollection
                                                                      .items[0]
                                                                      .productLogo
                                                                      .width ||
                                                                  undefined,
                                                              height:
                                                                  component
                                                                      .productCollection
                                                                      .items[0]
                                                                      .productLogo
                                                                      .height ||
                                                                  undefined,
                                                              size:
                                                                  component
                                                                      .productCollection
                                                                      .items[0]
                                                                      .productLogo
                                                                      .size ||
                                                                  0,
                                                              fileName:
                                                                  component
                                                                      .productCollection
                                                                      .items[0]
                                                                      .productLogo
                                                                      .fileName ||
                                                                  null,
                                                              contentType:
                                                                  component
                                                                      .productCollection
                                                                      .items[0]
                                                                      .productLogo
                                                                      .contentType ||
                                                                  undefined
                                                          }
                                                        : null,
                                                    dataBlueprintModule:
                                                        component.dataBlueprintModule ||
                                                        null
                                                } as CallToAction;
                                            case "QuestionComponent":
                                                return {
                                                    __typename:
                                                        "QuestionComponent",
                                                    questionTitle:
                                                        component.questionTitle ||
                                                        "",
                                                    answer: component.answer
                                                        ?.json
                                                        ? richTextToHTML(
                                                              component.answer
                                                                  ?.json
                                                          )
                                                        : null,
                                                    answerAsset:
                                                        component.answerAsset ||
                                                        null
                                                } as QuestionComponent;
                                            case "EmbeddedVideoAsset":
                                                return {
                                                    __typename:
                                                        "EmbeddedVideoAsset",
                                                    embeddedVideoAssetName:
                                                        component.embeddedVideoAssetName ||
                                                        "",
                                                    embedded:
                                                        component.embedded ||
                                                        "",
                                                    embedAssetOverlay:
                                                        component.embedAssetOverlay ||
                                                        null
                                                } as EmbedComponent;
                                            default:
                                                return {
                                                    __typename:
                                                        component?.__typename,
                                                    title: null,
                                                    valueProposition: null,
                                                    text: null,
                                                    url: null,
                                                    asset: null,
                                                    productName: null,
                                                    productLogo: null,
                                                    dataBlueprintModule: null
                                                } as CallToAction;
                                        }
                                    }
                                ) || []
                        };
                    }
                ) || null,
            nextBestAction:
                (articleDetails?.nextBestActionCollection?.items || []).length >
                0
                    ? ((articleDetails?.nextBestActionCollection?.items
                          ?.map(
                              // eslint-disable-next-line complexity
                              (nextAction) => {
                                  if (!nextAction) return null;
                                  switch (nextAction.__typename) {
                                      case "MicrositeArticle":
                                          return {
                                              __typename:
                                                  "MicrositeArticle" as const,
                                              articleName:
                                                  nextAction.articleName ||
                                                  null,
                                              title: nextAction.title || null,
                                              description:
                                                  nextAction.description ||
                                                  null,
                                              urlSlug: nextAction.urlSlug || "",
                                              articleBanner:
                                                  nextAction.articleBanner
                                                      ? {
                                                            description:
                                                                nextAction
                                                                    .articleBanner
                                                                    .description ||
                                                                undefined,
                                                            name:
                                                                nextAction
                                                                    .articleBanner
                                                                    .name || "",
                                                            url:
                                                                nextAction
                                                                    .articleBanner
                                                                    .url || "",
                                                            width:
                                                                nextAction
                                                                    .articleBanner
                                                                    .width ||
                                                                undefined,
                                                            height:
                                                                nextAction
                                                                    .articleBanner
                                                                    .height ||
                                                                undefined,
                                                            size:
                                                                nextAction
                                                                    .articleBanner
                                                                    .size || 0,
                                                            fileName:
                                                                nextAction
                                                                    .articleBanner
                                                                    .fileName ||
                                                                null,
                                                            contentType:
                                                                nextAction
                                                                    .articleBanner
                                                                    .contentType ||
                                                                undefined
                                                        }
                                                      : null
                                          } as NextBestAction;
                                      case "Tutorial":
                                          return {
                                              __typename: "Tutorial" as const,
                                              name: nextAction.name || null,
                                              title: nextAction.title || null,
                                              description:
                                                  nextAction.description ||
                                                  null,
                                              urlSlug: nextAction.urlSlug || "",
                                              tutorialBanner: nextAction
                                                  .tutorialBanner?.url
                                                  ? {
                                                        description:
                                                            nextAction
                                                                .tutorialBanner
                                                                .description ||
                                                            undefined,
                                                        name:
                                                            nextAction
                                                                .tutorialBanner
                                                                .name || "",
                                                        url:
                                                            nextAction
                                                                .tutorialBanner
                                                                .url || "",
                                                        width:
                                                            nextAction
                                                                .tutorialBanner
                                                                .width ||
                                                            undefined,
                                                        height:
                                                            nextAction
                                                                .tutorialBanner
                                                                .height ||
                                                            undefined,
                                                        size:
                                                            nextAction
                                                                .tutorialBanner
                                                                .size || 0,
                                                        fileName:
                                                            nextAction
                                                                .tutorialBanner
                                                                .fileName ||
                                                            null,
                                                        contentType:
                                                            nextAction
                                                                .tutorialBanner
                                                                .contentType ||
                                                            undefined
                                                    }
                                                  : null
                                          } as NextBestAction;
                                      default:
                                          return null;
                                  }
                              }
                          )
                          ?.filter(Boolean) || null) as NextBestAction[])
                    : null,
            relatedHub:
                articleDetails?.relatedHubCollection?.items.map((related) => ({
                    title: related?.title,
                    shortDescriptionOneLiner: related?.shortDescriptionOneLiner,
                    description: related?.description?.json
                        ? richTextToHTML(related?.description?.json)
                        : null
                })) || [],
            upNext: articleDetails?.upNext
                ? // eslint-disable-next-line complexity
                  (() => {
                      switch (articleDetails.upNext.__typename) {
                          case "MicrositeArticle":
                              return {
                                  __typename: "MicrositeArticle",
                                  articleName:
                                      articleDetails.upNext.articleName || null,
                                  articleTitle:
                                      articleDetails.upNext.articleTitle ||
                                      null,
                                  description:
                                      articleDetails.upNext.description || null,
                                  urlSlug: articleDetails.upNext.urlSlug || "",
                                  articleBanner: articleDetails.upNext
                                      .articleBanner
                                      ? {
                                            description:
                                                articleDetails.upNext
                                                    .articleBanner
                                                    .description || undefined,
                                            name:
                                                articleDetails.upNext
                                                    .articleBanner.name || "",
                                            url:
                                                articleDetails.upNext
                                                    .articleBanner.url || "",
                                            width:
                                                articleDetails.upNext
                                                    .articleBanner.width ||
                                                undefined,
                                            height:
                                                articleDetails.upNext
                                                    .articleBanner.height ||
                                                undefined,
                                            size:
                                                articleDetails.upNext
                                                    .articleBanner.size || 0,
                                            fileName:
                                                articleDetails.upNext
                                                    .articleBanner.fileName ||
                                                null,
                                            contentType:
                                                articleDetails.upNext
                                                    .articleBanner
                                                    .contentType || undefined
                                        }
                                      : null
                              };
                          case "Tutorial":
                              return {
                                  __typename: "Tutorial",
                                  tutorialName:
                                      articleDetails.upNext.tutorialName ||
                                      null,
                                  tutorialTitle:
                                      articleDetails.upNext.tutorialTitle ||
                                      null,
                                  description:
                                      articleDetails.upNext.description || null,
                                  urlSlug: articleDetails.upNext.urlSlug || "",
                                  tutorialBanner: articleDetails.upNext
                                      .tutorialBanner
                                      ? {
                                            description:
                                                articleDetails.upNext
                                                    .tutorialBanner
                                                    .description || undefined,
                                            name:
                                                articleDetails.upNext
                                                    .tutorialBanner.name || "",
                                            url:
                                                articleDetails.upNext
                                                    .tutorialBanner.url || "",
                                            width:
                                                articleDetails.upNext
                                                    .tutorialBanner.width ||
                                                undefined,
                                            height:
                                                articleDetails.upNext
                                                    .tutorialBanner.height ||
                                                undefined,
                                            size:
                                                articleDetails.upNext
                                                    .tutorialBanner.size || 0,
                                            fileName:
                                                articleDetails.upNext
                                                    .tutorialBanner.fileName ||
                                                null,
                                            contentType:
                                                articleDetails.upNext
                                                    .tutorialBanner
                                                    .contentType || undefined
                                        }
                                      : null
                              };
                          case "MicrositeTopicOverview":
                              return {
                                  __typename: "MicrositeTopicOverview",
                                  topicOverviewId:
                                      articleDetails.upNext.topicOverviewId,
                                  name: articleDetails.upNext.name || null,
                                  subtitle:
                                      articleDetails.upNext.subtitle || null,
                                  description:
                                      articleDetails.upNext.description || null,
                                  title: articleDetails.upNext.title || null,
                                  urlSlug: articleDetails.upNext.urlSlug || "",
                                  bannerImage:
                                      articleDetails.upNext.bannerImage || null
                              };
                          default:
                              return null;
                      }
                  })()
                : null,
            articleName: articleDetails?.articleRef?.articleName || null,
            title: articleDetails?.articleRef?.title || null,
            description: articleDetails?.articleRef?.description || null,
            urlSlug: articleDetails?.articleRef?.urlSlug || "",
            articleBanner: {
                description:
                    articleDetails?.articleRef?.articleBanner?.description ||
                    undefined,
                name: articleDetails?.articleRef?.articleBanner?.name || "",
                url: articleDetails?.articleRef?.articleBanner?.url || "",
                width:
                    articleDetails?.articleRef?.articleBanner?.width ||
                    undefined,
                height:
                    articleDetails?.articleRef?.articleBanner?.height ||
                    undefined,
                size: articleDetails?.articleRef?.articleBanner?.size || 0,
                fileName:
                    articleDetails?.articleRef?.articleBanner?.fileName || null,
                contentType:
                    articleDetails?.articleRef?.articleBanner?.contentType ||
                    undefined
            },
            contentHubSubscribe:
                articleDetails?.contentHubSubscribeCollection?.items.map(
                    (subscribe) => ({
                        subscribeComponentName:
                            subscribe?.subscribeComponentName || null,
                        valueProposition: subscribe?.valueProposition || null,
                        ctaButtonText: subscribe?.ctaButtonText || null,
                        successMessage: subscribe?.successMessage || null
                    })
                ) || [],
            productDiscussedCta: null,
            relatedTutorialCta: null,
            whitePaperCta: null,
            relatedProductFeatures: null,
            shareThisArticle: null
        }))[0] || ({} as AgileArticle)
    );
};

export const GetAgileArticleTitleDescription = (
    queryRef: PreloadedQuery<OperationType>
) => {
    const data = usePreloadedQuery<agileArticleOneQuery>(
        AgileArticleOneQuery,
        queryRef as PreloadedQuery<
            agileArticleOneQuery,
            Record<string, unknown>
        >
    );
    return {
        title: data.articleDetailsCollection?.items[0]?.articleRef?.title,
        description:
            data.articleDetailsCollection?.items[0]?.articleRef?.description
    };
};
